import Utils from "../utils/utils";
import Api from '../api/api';
import {ApiResponse} from "../api/type-declerations/response";


export interface FileUtilsInitialFileUploadArg {
    chunk: Blob,
    fileSize: number,
    fileName: string,
    onUploadProgress: ((event: any) => void)
}

export interface FileUtilsUploadFileResponse {
}

export interface FileUtilsInitialFileUploadCallback<T extends FileUtilsUploadFileResponse = FileUtilsUploadFileResponse> {
    (arg: FileUtilsInitialFileUploadArg): ApiResponse<T>
}

export interface FileUtilsOnProgressCallback {
    (progress: number): void
}

/**
 * The file utilities.
 */
class FileUtils {

    /**
     * The file chunk size.
     * @default 4MB
     */
    public static readonly fileChunkSize = 1024 * 1024 * 4;

    /**
     * The maximum upload file size.
     * @default 100MB
     */
    public static readonly maximumUploadFileSize = 1024 * 1024 * 100;

    /**
     * Uploads the remaining chunk of the file.
     *
     * @param file                  The file to upload.
     * @param initialFileUpload     The initial file upload function.
     * @param getFileGuid           The function that gets the file GUID.
     * @param onProgress            The progress event handler.
     * @returns                     The response.
     */
    public static async uploadFile<T extends FileUtilsUploadFileResponse>(
        file: File,
        initialFileUpload: FileUtilsInitialFileUploadCallback<T>,
        getFileGuid: (response: T) => string,
        onProgress: FileUtilsOnProgressCallback,
    ): Promise<T | Error | undefined> {
        const chunkSize = this.fileChunkSize;
        const totalChunks = Math.ceil(file.size / chunkSize);
        let chunkIndex = 0;
        let result: T | undefined = undefined;

        if (file.size > this.maximumUploadFileSize) {
            return new Error(`The file size exceeds the maximum upload file size of ${Utils.formatBytes(this.maximumUploadFileSize)}.`);
        }

        onProgress(0);

        /**
         * The progress event handler for the file upload.
         * @param event
         */
        const onUploadProgress = (event: any) => {
            const percentageDoneInThisIteration = ((event.loaded / (event.total ?? 1)) * 100);
            const previousDone = (chunkIndex / totalChunks) * 100;
            const progress = previousDone + (percentageDoneInThisIteration / totalChunks);
            onProgress(progress);
        }

        for (chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const chunk = file.slice(chunkIndex * chunkSize, (chunkIndex + 1) * chunkSize);
            if (chunkIndex === 0) {
                const response = await initialFileUpload({
                    chunk: chunk,
                    fileName: Utils.sanitizeFileName(file.name),
                    fileSize: file.size,
                    onUploadProgress,
                });
                if (!response?.resultFlag) {
                    return new Error(response?.message ?? "An error occurred while uploading the file.");
                }
                result = response?.data;
            } else {
                const response = await Api.uploadRemainingChunk({
                    nextChunk: chunk,
                    nextChunkFileName: Utils.sanitizeFileName(file.name),
                    fileId: getFileGuid(result!),
                }, onUploadProgress)
                if (!response?.resultFlag) {
                    return new Error(response?.message ?? "An error occurred while uploading the file.");
                }
            }
        }

        // if (!result) {
        //     return new Error("An error occurred while uploading the file.");
        // }

        return result;
    }

}


export default FileUtils;
